import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useSpring } from 'framer-motion';
import { ArrowRight, ChevronDown, Zap, Brain, Cloud, Palette } from 'lucide-react';
import NeuralCanvas from '../components/NeuralCanvas';
import TiltCard from '../components/TiltCard';
import Testimonials from '../components/Testimonials';

// Parallax image helper
const ParallaxImage = ({ src, alt, speed = 0.15 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);
  return (
    <div ref={ref} style={{ overflow: 'hidden', width: '100%', height: '100%', borderRadius: 'inherit' }}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y, width: '100%', height: '115%', objectFit: 'cover', willChange: 'transform' }}
      />
    </div>
  );
};

const services = [
  {
    num: '01', icon: <Zap size={28} />,
    title: 'Software Engineering',
    description: 'From custom enterprise systems to mobile apps and APIs  we engineer software that scales.',
    items: ['Custom Software', 'Web Applications', 'Enterprise Systems', 'Mobile Apps', 'API Dev'],
    color: '#00d4aa',
  },
  {
    num: '02', icon: <Brain size={28} />,
    title: 'AI & Machine Learning',
    description: 'We integrate intelligence into your core business  NLP, computer vision, full AI SaaS.',
    items: ['AI Integration', 'Chatbots & NLP', 'Data Analytics', 'Computer Vision', 'AI SaaS'],
    color: '#06b6d4',
  },
  {
    num: '03', icon: <Cloud size={28} />,
    title: 'Cloud & DevOps',
    description: 'Bulletproof infrastructure that scales, secured by design and maintained 24/7.',
    items: ['Infrastructure Setup', 'Security & Optimization', 'Maintenance & Support'],
    color: '#6366f1',
  },
  {
    num: '04', icon: <Palette size={28} />,
    title: 'UI/UX Design',
    description: 'Products people love  from strategy to interactive prototypes that convert.',
    items: ['Product Design', 'UX Strategy', 'Design Systems', 'Prototyping'],
    color: '#8b5cf6',
  },
];

const processSteps = [
  { num: '01', title: 'Discover', desc: 'Deep-dive into your business goals, technical landscape, and user needs.' },
  { num: '02', title: 'Architect', desc: 'Design the system blueprint  tech stack, data models, API contracts, and scalability plan.' },
  { num: '03', title: 'Build', desc: 'Iterative development with weekly demos, automated tests, and CI/CD.' },
  { num: '04', title: 'Deploy', desc: 'Zero-downtime launch with full monitoring, documentation, and post-deployment support.' },
];

const caseStudies = [
  {
    id: 'mediainfotech',
    title: 'MediaInfoTech',
    category: 'Digital Media Platform',
    desc: 'Enterprise-grade media intelligence and operations platform for digital content creators and agencies.',
    tags: ['React', 'Node.js', 'MongoDB', 'AI', 'AWS', 'CI/CD', 'Microservices'],
    img: 'https://images.unsplash.com/photo-1639094071972-a79473d1a7c6?w=600&q=80',
  },
  {
    id: 'clearledger',
    title: 'ClearLedger',
    category: 'FinTech SaaS Platform',
    desc: 'Automated financial reconciliation and regulatory reporting platform for SMB finance teams.',
    tags: ['React', 'Node.js', 'FinTech'],
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&q=80',
  },
  {
    id: 'neurohealth',
    title: 'NeuroHealth AI',
    category: 'Healthcare Analytics',
    desc: 'AI-powered patient analytics with 87% prediction accuracy reducing readmissions by 23%.',
    tags: ['Python', 'AI/ML', 'React'],
    img: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80',
  },
];

// Scroll hijack service scene
const ServiceScene = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start center', 'end center'] });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.2, 1, 1, 0.2]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.94, 1, 1, 0.94]);
  const x = useTransform(scrollYProgress, [0, 0.4, 1], [index % 2 === 0 ? -40 : 40, 0, 0]);

  const colorMap = {
    '#00d4aa': '0,212,170',
    '#06b6d4': '6,182,212',
    '#6366f1': '99,102,241',
    '#8b5cf6': '139,92,246',
  };
  const rgb = colorMap[service.color] || '0,212,170';

  return (
    <motion.div
      ref={ref}
      style={{ opacity, scale, x }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
      data-testid={`service-scene-${service.num}`}
    >
      <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.72rem', color: service.color, letterSpacing: '0.15em', display: 'block', marginBottom: '16px' }}>
          {service.num} / 04
        </span>
        <h3
          style={{
            fontFamily: 'Manrope',
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
            color: '#fff',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '16px',
          }}
        >
          {service.title}
        </h3>
        <p style={{ color: '#94a3b8', fontFamily: 'Inter', fontSize: '1rem', lineHeight: 1.8, marginBottom: '24px' }}>
          {service.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {service.items.map((item) => (
            <span key={item} className="skill-badge">{item}</span>
          ))}
        </div>
      </div>
      <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
        <TiltCard
          className="glass-card"
          style={{
            height: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `rgba(${rgb}, 0.04)`,
            border: `1px solid rgba(${rgb}, 0.15)`,
          }}
          intensity={8}
        >
          <div
            style={{
              width: '96px',
              height: '96px',
              borderRadius: '24px',
              background: `rgba(${rgb}, 0.1)`,
              border: `1px solid rgba(${rgb}, 0.25)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: service.color,
              boxShadow: `0 0 40px rgba(${rgb}, 0.12)`,
            }}
          >
            {React.cloneElement(service.icon, { size: 42 })}
          </div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `radial-gradient(circle at 50% 50%, rgba(${rgb},0.07) 0%, transparent 60%)`,
              pointerEvents: 'none',
            }}
          />
        </TiltCard>
      </div>
    </motion.div>
  );
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(heroScroll, [0, 1], [0, 80]);
  const heroOpacity = useTransform(heroScroll, [0, 0.7], [1, 0]);
  const canvasY = useTransform(heroScroll, [0, 1], [0, 40]);
  const glowY = useTransform(heroScroll, [0, 1], [0, -50]);

  return (
    <main data-testid="home-page">

      {/* ===== HERO ===== */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="hero-grid-bg"
        style={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background: '#0a0f1e',
        }}
      >
        {/* Parallax neural canvas */}
        <motion.div style={{ y: canvasY, position: 'absolute', inset: 0, opacity: 0.65 }}>
          <NeuralCanvas className="w-full h-full" />
        </motion.div>

        {/* Parallax glow orb */}
        <motion.div
          style={{
            y: glowY,
            position: 'absolute',
            top: '20%',
            right: '5%',
            width: 'min(60vw, 600px)',
            height: 'min(60vw, 600px)',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,212,170,0.07) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <motion.div
          style={{
            y: useTransform(heroScroll, [0, 1], [0, 30]),
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: 'min(42vw, 400px)',
            height: 'min(42vw, 400px)',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="max-w-7xl mx-auto px-6 md:px-12 w-full"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-32">
            <div>
              <motion.span
                className="section-label"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                // Engineering Intelligence
              </motion.span>
              <motion.h1
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 800,
                  fontSize: 'clamp(2.8rem, 6vw, 5rem)',
                  lineHeight: 1.0,
                  letterSpacing: '-0.035em',
                  color: '#ffffff',
                  marginBottom: '24px',
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
                data-testid="hero-headline"
              >
                We Build Software{' '}
                <span className="text-gradient-teal">That Thinks.</span>
              </motion.h1>
              <motion.p
                style={{
                  fontFamily: 'Inter',
                  color: '#94a3b8',
                  fontSize: 'clamp(1rem, 1.8vw, 1.15rem)',
                  lineHeight: 1.8,
                  maxWidth: '520px',
                  marginBottom: '40px',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
              >
                Custom software, AI systems, and scalable cloud platforms
                engineered for measurable business outcomes.
              </motion.p>
              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.65 }}
              >
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Start a Project <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-secondary" data-testid="hero-cta-secondary">
                  Explore Services
                </Link>
              </motion.div>
            </div>

                {/* Hero visual */}
                {/* Hero visual - Prominent Logo */}
                <motion.div
                  className="hidden lg:flex justify-center"
                  style={{ y: useTransform(heroScroll, [0, 1], [0, -30]) }}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.1, delay: 0.4 }}
                >
                  <div className="float-animation" style={{ position: 'relative', width: '480px', height: '480px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Outer glow background */}
                    <div
                      style={{
                        position: 'absolute',
                        width: '350px',
                        height: '350px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(0,212,170,0.15) 0%, rgba(0,212,170,0.05) 40%, transparent 70%)',
                        filter: 'blur(20px)',
                      }}
                    />
                    {/* Animated pulsing rings */}
                    {[0, 30, 60, 90].map((inset, i) => (
                      <div
                        key={i}
                        className="pulse-ring"
                        style={{
                          position: 'absolute',
                          inset: `${inset}px`,
                          borderRadius: '50%',
                          border: `2px solid rgba(0,212,170,${0.35 - i * 0.08})`,
                          animation: `pulse-ring 4s ease-out infinite ${i * 0.5}s`,
                        }}
                      />
                    ))}
                    {/* Static border rings */}
                    {[120, 140].map((inset, i) => (
                      <div
                        key={`static-${i}`}
                        style={{
                          position: 'absolute',
                          inset: `${inset}px`,
                          borderRadius: '50%',
                          border: `1px solid rgba(0,212,170,${0.2 - i * 0.05})`,
                        }}
                      />
                    ))}
                    {/* Logo image */}
                    <img
                      src="/logo-transparent.png"
                      alt="InsightsNode"
                      data-testid="hero-logo"
                      style={{
                        width: '280px',
                        height: '280px',
                        objectFit: 'contain',
                        filter: 'hue-rotate(-52deg) saturate(2.5) brightness(2) drop-shadow(0 0 50px rgba(0,212,170,0.8)) drop-shadow(0 0 100px rgba(0,212,170,0.4))',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    />
                  </div>
                </motion.div>
          </div>
        </motion.div>

        <motion.div
          style={{ position: 'absolute', bottom: '32px', left: '50%', x: '-50%' }}
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
        </motion.div>
      </section>

      {/* ===== SCROLL-HIJACK SERVICES ===== */}
      <section
        data-testid="services-scroll"
        style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0a0f1e', borderTop: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, margin: '-80px' }}
            style={{ marginBottom: '80px' }}
          >
            <span className="section-label">// What We Do</span>
            <h2
              style={{
                fontFamily: 'Manrope',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                letterSpacing: '-0.03em',
                color: '#fff',
                lineHeight: 1.1,
              }}
            >
              Four Disciplines,
              <span className="text-gradient-teal"> One Vision.</span>
            </h2>
          </motion.div>

          <div className="flex flex-col gap-32">
            {services.map((s, i) => (
              <ServiceScene key={s.num} service={s} index={i} />
            ))}
          </div>

          <div className="flex justify-center mt-20">
            <Link to="/services" className="btn-secondary" data-testid="services-cta">
              View Full Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section
        data-testid="process-section"
        style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0d1b35' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, margin: '-80px' }}
            style={{ marginBottom: '80px' }}
          >
            <span className="section-label">// How We Work</span>
            <h2
              style={{
                fontFamily: 'Manrope',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                letterSpacing: '-0.03em',
                color: '#fff',
                lineHeight: 1.1,
              }}
            >
              From Concept to
              <span className="text-gradient-teal"> Launch.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                viewport={{ once: true, margin: '-60px' }}
                data-testid={`process-step-${step.num}`}
              >
                <TiltCard
                  className="glass-card"
                  style={{ padding: '36px', height: '100%' }}
                  intensity={6}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: 'rgba(0,212,170,0.07)',
                      border: '1px solid rgba(0,212,170,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Manrope',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#00d4aa',
                      marginBottom: '20px',
                    }}
                  >
                    {step.num}
                  </div>
                  <h3
                    style={{
                      fontFamily: 'Manrope',
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      color: '#fff',
                      marginBottom: '10px',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {step.title}
                  </h3>
                  <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.88rem', lineHeight: 1.7 }}>
                    {step.desc}
                  </p>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CASE STUDIES PREVIEW ===== */}
      <section
        data-testid="case-studies-section"
        style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0a0f1e' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, margin: '-80px' }}
            style={{ marginBottom: '72px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '24px' }}
          >
            <div>
              <span className="section-label">// Our Work</span>
              <h2
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 800,
                  fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                  letterSpacing: '-0.03em',
                  color: '#fff',
                  lineHeight: 1.1,
                }}
              >
                Projects That
                <span className="text-gradient-teal"> Deliver.</span>
              </h2>
            </div>
            <Link to="/work" className="btn-secondary">
              View All Work <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.map((cs, i) => (
              <motion.div
                key={cs.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                viewport={{ once: true, margin: '-60px' }}
              >
                <TiltCard
                  className="glass-card"
                  style={{ overflow: 'hidden', cursor: 'pointer' }}
                  intensity={8}
                  data-testid={`case-study-card-${cs.id}`}
                >
                  <div style={{ height: '210px', overflow: 'hidden', borderRadius: 'inherit' }}>
                    <ParallaxImage src={cs.img} alt={cs.title} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,15,30,0.7) 0%, transparent 60%)' }} />
                  </div>
                  <div style={{ padding: '28px' }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.7rem', color: '#00d4aa', letterSpacing: '0.1em', display: 'block', marginBottom: '8px' }}>
                      {cs.category}
                    </span>
                    <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '1.2rem', color: '#fff', marginBottom: '8px', letterSpacing: '-0.01em' }}>
                      {cs.title}
                    </h3>
                    <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '14px' }}>
                      {cs.desc}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {cs.tags.map((t) => <span key={t} className="skill-badge">{t}</span>)}
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <Testimonials />

      {/* ===== CTA ===== */}
      <section
        data-testid="cta-section"
        style={{
          padding: 'clamp(88px, 12vw, 140px) 0',
          background: '#0d1b35',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(ellipse at 50% 50%, rgba(0,212,170,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="max-w-4xl mx-auto px-6">
          <motion.span
            className="section-label"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            style={{ display: 'block', textAlign: 'center', marginBottom: '24px' }}
          >
            // Ready to Scale?
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              fontFamily: 'Manrope',
              fontWeight: 800,
              fontSize: 'clamp(2.2rem, 5vw, 4rem)',
              letterSpacing: '-0.04em',
              color: '#fff',
              lineHeight: 1.05,
              marginBottom: '32px',
            }}
          >
            Let’s Engineer Your
            <br />
            <span className="text-gradient-teal">Next Breakthrough.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            style={{
              color: '#64748b',
              fontFamily: 'Inter',
              fontSize: '1.05rem',
              lineHeight: 1.75,
              marginBottom: '40px',
              maxWidth: '480px',
              margin: '0 auto 40px',
            }}
          >
            Tell us what you’re building. We’ll make it remarkable.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            viewport={{ once: true }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <Link to="/contact" className="btn-primary" data-testid="cta-book-btn" style={{ fontSize: '1rem', padding: '16px 40px' }}>
              Book a Consultation <ArrowRight size={18} />
            </Link>
            <Link to="/work" className="btn-secondary" style={{ fontSize: '1rem', padding: '15px 40px' }}>
              See Our Work
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
